import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import Section from '@components/Section'
import Input from '@components/Input'
import Tooltip from '@components/Tooltip'
import Button from '@components/Button'
import PhoneInput, { type PhoneInputMeta } from '@components/PhoneInput'
import Radio from '@components/Radio'
import Spinner from '@components/Spinner'

import { isEmailValid } from '@/util/helpers'
import MessagePreviewModal from '@components/MessagePreviewModal'

export interface AddContactFormProps {
  onSubmit: (contact: {
    name: string
    phone: string
    email: string
    alias: string
    language: 'en' | 'es'
  }) => Promise<boolean>
  labelStyle?: string
  /** Overrides the default alias-field tooltip; pass a `<Tooltip>` (e.g. recolored for a dark surface). */
  aliasTooltip?: ReactNode
  buttonVariant?: 'alt' | 'cta' | 'highlight' | 'success' | 'danger' | undefined
}

export default function AddContactForm({ onSubmit, labelStyle, aliasTooltip, buttonVariant }: AddContactFormProps) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [isPhoneValid, setIsPhoneValid] = useState(true)
  const [email, setEmail] = useState('')
  const [alias, setAlias] = useState('')
  const [language, setLanguage] = useState<'en' | 'es'>('en')
  const [isPending, setIsPending] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  function validateForm(): boolean {
    if (!name.trim()) {
      return false
    }

    const hasPhone = phone.trim().length > 0
    const hasEmail = email.trim().length > 0

    if (!hasPhone && !hasEmail) {
      return false
    }

    if (hasPhone && !isPhoneValid) {
      return false
    }

    if (hasEmail && !isEmailValid(email)) {
      return false
    }

    return true
  }

  const isFormValid = validateForm()

  async function handleSubmit() {
    if (isFormValid) {
      setIsPending(true)
      const success = await onSubmit({
        name,
        phone,
        email,
        alias,
        language,
      })
      if (success) {
        setName('')
        setPhone('')
        setIsPhoneValid(true)
        setEmail('')
        setAlias('')
        setLanguage('en')
      }
      setIsPending(false)
    }
  }

  return (
    <Section wrapperStyle={'!bg-transparent !shadow-none !border-none !my-0 text-cta-txt pt-2 mb-4 w-full'}>
      <div className='flex flex-col gap-4 sm:gap-5 md:gap-6'>
        <div className='flex flex-col sm:grid lg:grid-cols-2 sm:items-end gap-4 sm:gap-5 md:gap-6'>
          <div className='w-full'>
            <Input
              id='name'
              label={t('content.components.input_contact.name_field_title')}
              labelStyle={labelStyle}
              type='text'
              placeholder={t('content.components.input_contact.name_field_placeholder')}
              value={name}
              onInput={setName}
              inputStyle='focus:!ring-cta-active'
            />
          </div>
          <div className='w-full'>
            <PhoneInput
              id='phone'
              label={t('content.components.input_contact.phone_field_title')}
              name='phone'
              value={phone}
              availableCountries={['US', 'CA', 'MX']}
              placeholder={t('content.components.input_contact.phone_field_placeholder')}
              labelStyle={labelStyle}
              borderStyle='!border-2 focus-within:!border-transparent focus-within:!ring-2 focus-within:!ring-cta-active !focus:outline'
              onChange={(value, meta: PhoneInputMeta) => {
                setPhone(value || '')
                setIsPhoneValid(meta.isValid)
              }}
            />
          </div>
          <div className='w-full'>
            <Input
              id='alias'
              label={t('content.components.input_contact.nickname_field_title')}
              tooltip={aliasTooltip ?? <Tooltip content={t('content.components.input_contact.nickname_tooltip')} />}
              labelStyle={labelStyle}
              optional
              value={alias}
              placeholder={t('content.components.input_contact.nickname_field_placeholder')}
              onInput={setAlias}
              inputStyle='focus:!ring-cta-active'
            />
          </div>
          <div className='w-full'>
            <div className='text-cta-txt mb-2'>{t('content.components.input_contact.language_field_title')}</div>
            <div className='flex flex-wrap gap-3 sm:gap-4 sm:pt-2.5 sm:pb-3'>
              <Radio
                label='English'
                value='en'
                name='language'
                checked={language === 'en'}
                onChange={(value) => setLanguage(value as 'en' | 'es')}
                labelStyles={labelStyle}
              />
              <Radio
                label='Español'
                value='es'
                name='language'
                checked={language === 'es'}
                onChange={(value) => setLanguage(value as 'en' | 'es')}
                labelStyles={labelStyle}
              />
            </div>
          </div>
          <div className='w-full'>
            <Input
              id='email'
              label={t('content.components.input_contact.email_field_title')}
              labelStyle={labelStyle}
              optional
              value={email}
              placeholder={t('content.components.input_contact.email_field_placeholder')}
              onInput={setEmail}
              inputStyle='focus:!ring-cta-active'
            />
          </div>
          <div className='w-full'>
            <Button
              label={isPending ? '' : t('content.components.input_contact.send_invitation_button')}
              variant={buttonVariant || 'success'}
              fullwidth
              size='xl'
              disabled={!isFormValid || isPending}
              icon={isPending ? <Spinner size='sm' /> : null}
              onClick={handleSubmit}
            />
          </div>
        </div>
        <div className='flex flex-col gap-y-4 sm:gap-y-5 md:gap-y-6'>
          <div className={`text-xs opacity-80 text-neutral-800 ${labelStyle}`}>
            {t('content.components.input_contact.sms_disclaimer')}
          </div>
          <div className='w-full text-center'>
            <button
              type='button'
              onClick={() => setShowPreview(true)}
              className={`text-sm underline cursor-pointer text-cta hover:text-cta/80 ${labelStyle}`}
            >
              {t('content.components.message_preview.link')}
            </button>
          </div>
        </div>
      </div>
      <MessagePreviewModal show={showPreview} onClose={() => setShowPreview(false)} language={language} />
    </Section>
  )
}
